// "What is covered, and by what" — one table per practice: each data stream, the
// source that covers it here, how it arrives, how fresh it is, how many records sit
// behind it, and its readiness. Rendered on Setup > Data and on Practice Details; both
// read GET /api/practices/<id>/source-coverage, which carries labels, statuses, counts
// and timestamps and never a patient.
//
// The vocabulary (live / stale / partial / needs a pull / blocked / n/a; API feed,
// report upload, email ingest, manual entry; line / claim / visit / record ...) is the
// backend's data_readiness module, the same computation the super_admin Data readiness
// grid shows for every practice at once, so the two never disagree.

import React, { useEffect, useState } from "react";

import "./SourceCoverage.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const STATUS_LABELS = {
  live: "Live",
  stale: "Stale",
  partial: "Partial",
  needs_pull: "Needs a pull",
  blocked: "Blocked",
  not_applicable: "n/a",
};

const STATUS_TONE = {
  live: "green",
  stale: "amber",
  partial: "blue",
  needs_pull: "grey",
  blocked: "red",
  not_applicable: "muted",
};

export function coverageUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/source-coverage`;
}

// The freshness cell: "9d ago" with the stamp on hover, or an em dash.
export function freshnessText(item) {
  if (!item || !item.last_updated_at) return "—";
  return item.age ? `${item.age} ago` : item.last_updated_at;
}

// "9 live, 1 stale, 2 partial, 1 needs a pull, 0 blocked, 1 n/a".
export function summaryText(summary) {
  if (!summary) return "";
  const n = (key) => summary[key] || 0;
  return [
    `${n("live")} live`,
    `${n("stale")} stale`,
    `${n("partial")} partial`,
    `${n("needs_pull")} need${n("needs_pull") === 1 ? "s" : ""} a pull`,
    `${n("blocked")} blocked`,
    `${n("not_applicable")} n/a`,
  ].join(", ");
}

export function fetchCoverage(practiceId) {
  return fetch(coverageUrl(practiceId), { credentials: "include" }).then(async (response) => {
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error((body && body.error) || "Could not load source coverage.");
    }
    if (!body || !Array.isArray(body.items) || !body.summary) {
      throw new Error("Source coverage came back in an unexpected shape.");
    }
    return body;
  });
}

export function StatusBadge({ status, labels }) {
  const tone = STATUS_TONE[status] || "grey";
  return (
    <span className={`coverage-badge coverage-badge-${tone}`}>
      {(labels && labels[status]) || STATUS_LABELS[status] || status}
    </span>
  );
}

export default function SourceCoverageTable({ practiceId, title = "What is covered, and by what", compact = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setError(null);
    fetchCoverage(practiceId)
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId]);

  if (!practiceId) return null;

  const labels = (data && data.labels) || {};
  const ingressLabel = (key) => (labels.ingress && labels.ingress[key]) || key;
  const granularityLabel = (key) => (labels.granularity && labels.granularity[key]) || key;

  return (
    <div className={`coverage-card${compact ? " coverage-card-compact" : ""}`} data-testid="source-coverage">
      <div className="coverage-header">
        <h2>{title}</h2>
        {data && (
          <span className="coverage-summary">
            {data.source_kind_label}
            {" · "}
            {summaryText(data.summary)}
          </span>
        )}
      </div>
      {error && (
        <div className="coverage-error">
          {error}
          <button type="button" className="coverage-dismiss" aria-label="Dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}
      {!data && !error && <p className="coverage-muted">Loading…</p>}
      {data && (
        <table className="coverage-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Covered by</th>
              <th>Status</th>
              <th>Freshness</th>
              <th>Records</th>
              <th>Note</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <tr key={item.key} className={`coverage-row coverage-row-${item.status}`}>
                <td className="coverage-item">
                  {item.label}
                  {item.status !== "not_applicable" && (
                    <span className="coverage-granularity">{granularityLabel(item.granularity)}</span>
                  )}
                </td>
                <td>
                  {item.source_label}
                  {item.ingress && item.ingress !== "none" && (
                    <span className="coverage-ingress">{ingressLabel(item.ingress)}</span>
                  )}
                </td>
                <td>
                  <StatusBadge status={item.status} labels={labels.status} />
                </td>
                <td className="coverage-fresh" title={item.last_updated_at || ""}>
                  {freshnessText(item)}
                </td>
                <td className="coverage-count">
                  {item.record_count === null || item.record_count === undefined
                    ? "—"
                    : Number(item.record_count).toLocaleString("en-US")}
                </td>
                <td className="coverage-note">
                  {item.note}
                  {Array.isArray(item.details) && item.details.length > 0 && (
                    <div className="coverage-details">
                      {item.details.map((d) => (
                        <span key={d.key} className="coverage-detail" title={d.note}>
                          {d.label}: {d.status.replace("_", " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
